import React, { useEffect, useRef } from 'react';
import { Building2, Wrench, GraduationCap, ArrowUpRight, Check } from 'lucide-react';
import { trackMatomoEvent } from '../lib/matomo';

const SITES = {
  meteris: {
    label: 'Une solution pour mon activité',
    sublabel: 'Infogérance, téléphonie, cybersécurité, copieurs, IA, serveurs',
    icon: Building2,
    href: 'https://meteris.fr/',
    matomoAction: 'to-meteris',
  },
  'meaux-info': {
    label: 'Une réparation ponctuelle',
    sublabel: 'Dépannage, antivirus, panne PC, réparation matérielle',
    icon: Wrench,
    href: 'https://atelier.meteris.fr/',
    matomoAction: 'to-meaux-info',
  },
  'mcf-formation': {
    label: 'Une formation',
    sublabel: 'Formations Qualiopi, TOSA, bureautique, IA, sur mesure',
    icon: GraduationCap,
    href: 'https://mcf-formation.fr/',
    matomoAction: 'to-mcf-formation',
  },
};

// Ordre FIXE sur les 3 sites (mémoire spatiale + cohérence cross-domaine).
// La pill active est identifiée par son style visuel (filled+check), pas par sa position.
const PILL_ORDER = ['meteris', 'meaux-info', 'mcf-formation'];

export default function PersonaSwitcher({ currentSite }) {
  const asideRef = useRef(null);

  // Expose la hauteur réelle du bandeau via --persona-h (mobile wrap = ~143px, desktop = 72px).
  // Consommée par Navbar (top) et PrendreRdv (mt) pour rester synchronisé sans hardcoder.
  useEffect(() => {
    if (!asideRef.current || typeof ResizeObserver === 'undefined') return;
    const el = asideRef.current;
    const ro = new ResizeObserver((entries) => {
      const h = entries[0]?.contentRect?.height;
      if (h) document.documentElement.style.setProperty('--persona-h', `${h}px`);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  if (!SITES[currentSite]) {
    return null;
  }

  return (
    <aside
      ref={asideRef}
      role="navigation"
      aria-label="Aiguillage entre les sites METERIS, l'atelier de réparation et MCF Formation"
      className="fixed top-0 inset-x-0 z-[60] min-h-[72px] bg-[#0E1116] border-b border-white/10"
      data-testid="persona-switcher"
    >
      <div className="max-w-7xl mx-auto min-h-[72px] px-4 md:px-6 py-2 md:py-0 flex flex-wrap items-center justify-center gap-2 md:gap-4 text-sm">
        <span className="text-slate-400 hidden md:inline whitespace-nowrap font-medium">
          Vous cherchez :
        </span>

        {PILL_ORDER.map((key) => {
          const site = SITES[key];
          const Icon = site.icon;
          const isActive = key === currentSite;

          const handleClick = () => {
            if (isActive) return;
            trackMatomoEvent(
              'persona-switch',
              site.matomoAction,
              typeof window !== 'undefined' ? window.location.pathname : '/'
            );
          };

          return (
            <div key={key} className="flex flex-col items-start gap-1 min-w-0">
              {isActive ? (
                <span
                  className="inline-flex items-center gap-2 px-3 md:px-3.5 py-1.5 rounded-full
                             bg-[#0099D6] border border-[#0099D6] text-white font-semibold whitespace-nowrap
                             cursor-default shadow-[0_0_20px_rgba(0,153,214,0.25)]"
                  data-testid={`persona-pill-${key}`}
                  aria-current="page"
                >
                  <Icon className="w-4 h-4 shrink-0" aria-hidden="true" />
                  <span>{site.label}</span>
                  <Check className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                </span>
              ) : (
                <a
                  href={site.href}
                  rel="alternate"
                  onClick={handleClick}
                  className="inline-flex items-center gap-2 px-3 md:px-3.5 py-1.5 rounded-full
                             border border-white/15 text-slate-200 font-medium whitespace-nowrap
                             hover:text-white hover:border-white/30 hover:bg-white/5 transition-colors"
                  data-testid={`persona-pill-${key}`}
                >
                  <Icon className="w-4 h-4 shrink-0" aria-hidden="true" />
                  <span>{site.label}</span>
                  <ArrowUpRight className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                </a>
              )}
              <span className="text-xs text-slate-400 pl-3.5 truncate max-w-full hidden md:block">
                {site.sublabel}
              </span>
            </div>
          );
        })}
      </div>
    </aside>
  );
}
